(() => {
  if (customElements.get('blog-section-carousel')) return;

  class BlogSectionCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.cards = Array.from(this.querySelectorAll('[data-carousel-card]'));
      this.controls = this.querySelector('[data-carousel-controls]');
      this.pagination = this.querySelector('[data-carousel-pagination]');
      this.dots = [];
      this.pageOffsets = [0];
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.activeIndex = 0;
      this.scrollFrame = null;

      this.handleClick = this.onClick.bind(this);
      this.handleScroll = this.onScroll.bind(this);
      this.handleResize = this.updateMetrics.bind(this);

      this.addEventListener('click', this.handleClick);
      this.track?.addEventListener('scroll', this.handleScroll, { passive: true });

      if ('ResizeObserver' in window && this.track) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.handleResize);
      }

      this.updateMetrics();
      this.loadMissingArticles();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.track?.removeEventListener('scroll', this.handleScroll);
      this.resizeObserver?.disconnect();
      this.articleRequestController?.abort();

      if (!this.resizeObserver) window.removeEventListener('resize', this.handleResize);

      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    }

    onClick(event) {
      const viewAll = event.target.closest('[data-view-all]');
      if (viewAll && this.contains(viewAll)) {
        this.handleViewAll(event, viewAll);
        return;
      }

      const dot = event.target.closest('[data-carousel-dot]');
      if (dot && this.contains(dot)) {
        this.goToSlide(Number(dot.dataset.carouselDot));
        return;
      }

      if (event.target.closest('[data-carousel-previous]')) {
        this.goToSlide(this.activeIndex - 1);
      } else if (event.target.closest('[data-carousel-next]')) {
        this.goToSlide(this.activeIndex + 1);
      }
    }

    onScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.updateState();
        this.scrollFrame = null;
      });
    }

    calculatePageOffsets() {
      if (!this.track || this.cards.length <= 1) return [0];

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (maxScroll <= 1) return [0];

      const firstOffset = this.cards[0].offsetLeft;
      const cardStep = this.cards[1].offsetLeft - firstOffset;
      if (cardStep <= 0) return [0, maxScroll];

      const offsets = [0];

      for (let offset = cardStep; offset < maxScroll - 1; offset += cardStep) {
        offsets.push(offset);
      }

      offsets.push(maxScroll);
      return offsets;
    }

    renderPagination() {
      if (!this.pagination || this.dots.length === this.pageOffsets.length) return;

      const fragment = document.createDocumentFragment();

      this.pageOffsets.forEach((offset, index) => {
        const dot = document.createElement('button');
        dot.className = 'blog-section-carousel__dot';
        dot.type = 'button';
        dot.dataset.carouselDot = index;
        dot.setAttribute('aria-label', `Go to article page ${index + 1}`);
        fragment.appendChild(dot);
      });

      this.pagination.replaceChildren(fragment);
      this.dots = Array.from(this.pagination.querySelectorAll('[data-carousel-dot]'));
    }

    getClosestIndex() {
      if (!this.track || this.pageOffsets.length === 0) return 0;

      return this.pageOffsets.reduce(
        (closest, offset, index) => {
          const distance = Math.abs(offset - this.track.scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        },
        { index: 0, distance: Number.POSITIVE_INFINITY }
      ).index;
    }

    updateMetrics() {
      this.pageOffsets = this.calculatePageOffsets();
      this.renderPagination();

      if (this.controls) {
        this.controls.hidden = this.cards.length <= 1 || this.pageOffsets.length <= 1;
      }

      this.updateState();
    }

    updateState() {
      this.activeIndex = this.getClosestIndex();
      this.renderState();
    }

    renderState() {
      this.dots.forEach((dot, index) => {
        const isActive = index === this.activeIndex;
        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex >= this.pageOffsets.length - 1;
    }

    goToSlide(index) {
      if (!this.track || this.pageOffsets.length === 0) return;

      const targetIndex = Math.max(0, Math.min(index, this.pageOffsets.length - 1));
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollTo({
        left: this.pageOffsets[targetIndex],
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });

      this.activeIndex = targetIndex;
      this.renderState();
    }

    async loadMissingArticles() {
      const shouldLoad =
        this.dataset.autoArticles === 'true' &&
        this.dataset.filterContext !== 'true' &&
        Boolean(this.dataset.filterTag) &&
        Boolean(this.dataset.filterUrl) &&
        Boolean(this.dataset.sectionId) &&
        this.cards.length < 4 &&
        !this.hasRequestedArticles;

      if (!shouldLoad) return;

      this.hasRequestedArticles = true;
      this.articleRequestController = new AbortController();
      this.track?.setAttribute('aria-busy', 'true');

      try {
        const requestUrl = new URL(this.dataset.filterUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          signal: this.articleRequestController.signal,
        });

        if (!response.ok) throw new Error(`Article request failed: ${response.status}`);

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newSection = html.querySelector('blog-section-carousel');
        const sourceCardCount = newSection?.querySelectorAll('[data-carousel-card]').length || 0;

        if (!newSection || sourceCardCount < this.cards.length) return;

        this.replaceWith(newSection);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        this.track?.removeAttribute('aria-busy');
      }
    }

    handleViewAll(event, viewAll) {
      const targetPath = new URL(viewAll.href, window.location.origin).pathname;
      const headerTag = Array.from(document.querySelectorAll('.blog-header__tag')).find((tag) => {
        return new URL(tag.href, window.location.origin).pathname === targetPath;
      });

      event.preventDefault();

      if (headerTag) {
        headerTag.click();
        return;
      }

      const filterResults = document.querySelector('blog-filter-results');
      if (filterResults?.renderUrl) {
        const header = document.querySelector('[data-blog-header]');
        header?.querySelectorAll('.blog-header__tag').forEach((tag) => {
          const isActive = new URL(tag.href, window.location.origin).pathname === targetPath;
          tag.classList.toggle('is-active', isActive);
          if (isActive) tag.setAttribute('aria-current', 'page');
          else tag.removeAttribute('aria-current');
        });

        filterResults.renderUrl(viewAll.href, { scrollToResults: true }).catch((error) => {
          if (error?.name !== 'AbortError') window.location.assign(viewAll.href);
        });
        return;
      }

      window.location.assign(viewAll.href);
    }

    async blogUpdate(url) {
      const filterResults = document.querySelector('blog-filter-results');
      if (filterResults?.renderUrl) {
        return filterResults.renderUrl(url, { scrollToResults: true });
      }

      window.location.assign(url);
    }
  }

  customElements.define('blog-section-carousel', BlogSectionCarousel);
})();
